import { Loader2, X } from "lucide-react";
import { Button } from "./ui/button";
import { Form, useFetcher } from "@remix-run/react";
import { AnimatePresence, motion } from "motion/react";

function calculateDaysRemaining(trialEndTimeSeconds: number) {
  const currentTimeMs = Date.now() / 1000;
  const remainingTimeMs = trialEndTimeSeconds - currentTimeMs;
  const millisecondsInADay = 24 * 60 * 60;
  const daysRemaining = remainingTimeMs / millisecondsInADay;

  return Math.ceil(daysRemaining); // Use Math.ceil to round up to the nearest whole number
}

type TrialBannerProps = {
  trialEnd: number | null;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hasShownSurvey: boolean;
};

export default function TrailBanner({
  trialEnd,
  isOpen,
  setIsOpen,
  hasShownSurvey,
}: TrialBannerProps) {
  const fetcher = useFetcher({ key: "show-survey" });

  if (!trialEnd) return null;

  const isExpired = trialEnd <= Date.now() / 1000;
  const daysRemaining = calculateDaysRemaining(trialEnd);

  if (daysRemaining >= 14) return null;

  return (
    <>
      <AnimatePresence>
        {!hasShownSurvey && isOpen && (
          <motion.div
            key="request-feedback"
            initial={{ opacity: 0, translateY: 8 }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: 8, transition: { delay: 0 } }}
            transition={{ delay: 1 }}
            className="lex-col fixed bottom-0 right-8 z-20 flex w-[300px] flex-col items-center justify-center gap-4 rounded rounded-b-none border-[1.5px] border-b-0 border-emerald-500 bg-neutral-50 px-6 py-5 shadow-lg"
          >
            {!isExpired ? (
              <p className="text-nowrap text-center">
                Your trial ends in <strong>{`${daysRemaining}`} days</strong>.{" "}
              </p>
            ) : (
              <p className="text-nowrap text-center">
                Your trial <strong>has expired</strong>.{" "}
              </p>
            )}
            {/* TODO - uncomment on launch */}
            {/* <Button variant="link" asChild>
          <Link to="/billing" prefetch="intent">
            Start your subscription today
          </Link>
        </Button> */}
            <Form
              action="/feedback"
              method="post"
              className="inline-block w-full"
              navigate={false}
              fetcherKey="show-survey"
            >
              <Button
                type="submit"
                disabled={
                  fetcher.state === "submitting" || fetcher.state === "loading"
                }
                className="h-fit w-full p-3"
              >
                {fetcher.state === "submitting" ||
                fetcher.state === "loading" ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  "Give us feedback"
                )}
              </Button>
            </Form>
            <Button
              className="absolute right-0 top-0 h-[35px] w-[35px] -translate-y-1/2 translate-x-1/2 rounded-full border-[1.5px] border-emerald-500 bg-neutral-50 p-0 hover:bg-neutral-50"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-6 w-6 text-black" strokeWidth={1.2} />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
      {hasShownSurvey && <div className="shownSurvey invisible h-0 w-0" />}
    </>
  );
}
